import { FC, ReactNode } from "react";
import Typography from "../Typography";

type RivalComparisonProps = {
  sectionTitle: ReactNode;
  intro?: ReactNode;
  rivals: string[];
  rows: { label: string; klap: ReactNode; rivals: ReactNode[] }[];
  sources: ReactNode;
};

// Klap against named rivals, with a dated source line under the table. The
// table scrolls inside its own frame so the page never scrolls sideways on a
// phone.
const RivalComparison: FC<RivalComparisonProps> = ({
  sectionTitle,
  intro,
  rivals,
  rows,
  sources,
}) => {
  return (
    <section className="max-w-5xl px-3 md:px-12 mx-auto my-32">
      <Typography variant="h2" component="h2" className="text-center">
        {sectionTitle}
      </Typography>
      {intro && (
        <Typography
          variant="body1"
          className="mt-8 text-slate-500 leading-relaxed text-center max-w-3xl mx-auto"
        >
          {intro}
        </Typography>
      )}
      <div className="mt-12 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[640px] text-left">
          <thead className="border-b border-slate-200">
            <tr>
              <td className="px-5 py-4" />
              <th
                scope="col"
                className="px-5 py-4 font-display text-base font-semibold text-violet-600 bg-violet-50"
              >
                Klap
              </th>
              {rivals.map((rival) => (
                <th
                  key={rival}
                  scope="col"
                  className="px-5 py-4 font-display text-base font-semibold text-slate-900"
                >
                  {rival}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {rows.map((row) => (
              <tr key={row.label}>
                <th
                  scope="row"
                  className="px-5 py-4 align-top font-display text-sm font-medium text-slate-900"
                >
                  {row.label}
                </th>
                <td className="px-5 py-4 align-top font-display text-sm font-medium text-slate-900 bg-violet-50 leading-relaxed">
                  {row.klap}
                </td>
                {row.rivals.map((cell, index) => (
                  <td
                    key={rivals[index]}
                    className="px-5 py-4 align-top font-display text-sm text-slate-600 leading-relaxed"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Typography variant="caption" className="mt-4 text-slate-500">
        {sources}
      </Typography>
    </section>
  );
};

export default RivalComparison;
